<template>
  <pf-input-group class="x-input">
    <pf-text-input-group-utilities v-if="$slots.prepend || withCheckbox">
      <input v-if="withCheckbox" v-model="effectiveChecked" type="checkbox" :name="withCheckbox === true ? undefined : withCheckbox" value="1">
      <slot name="prepend">{{ prepend }}</slot>
    </pf-text-input-group-utilities>

    <pf-text-input
      v-if="inputType !== 'hidden' && inputType !== 'color' && inputType !== 'file' && inputType !== 'range'"
      v-bind="$attrs"
      ref="input"
      key="input"
      :model-value="inputValue"
      :type="inputType"
      :step="step"
      :pattern="pattern ? pattern : typePattern"
      :title="title ? title : typeTitle"
      :disabled="effectiveDisabled"
      @change="nativeErrors ? reportValidity() : checkValidity()"
      @blur="checkValidity"
      @invalid="isInvalid = true"
      @input="change"
    />

    <slot name="append">
      <pf-input-group-text v-if="append">
        {{ append }}
      </pf-input-group-text>
      <pf-input-group-text v-if="icon">
        <pf3-icon :name="icon" />
      </pf-input-group-text>
    </slot>

    <pf-tooltip v-if="type == 'new-password'">
      <pf-button
        :disabled="effectiveDisabled"
        @click="generatePassword"
      >
        <pf3-icon name="pficon-refresh" />
      </pf-button>
      <template #content>Genera nuova password</template>
    </pf-tooltip>

    <pf-tooltip v-if="resetValue !== undefined">
      <pf-button
        :disabled="effectiveDisabled || resetValue === value || undefined"
        @click="reset"
      >
        <pf3-icon name="fa-undo" />
      </pf-button>
      <template #content>Reset</template>
    </pf-tooltip>

    <slot />
  </pf-input-group>

  <transition>
    <pf-form-helper-text v-if="help || (isInvalid && !nativeErrors && (title || typeTitle))">
      <pf-helper-text>
        <pf-helper-text-item v-if="isInvalid && !nativeErrors && (title || typeTitle)" variant="error">
          {{ title ? title : typeTitle }}
        </pf-helper-text-item>
        <pf-helper-text v-else>{{ help }}</pf-helper-text>
      </pf-helper-text>
    </pf-form-helper-text>
  </transition>
</template>

<style lang="scss">
.x-input .help-block {
  &.v-enter,
  &.v-leave-to {
    line-height: 0;
  }
}
</style>

<script lang="ts">
const PWD_NUMBERS = 3;
const PWD_UPPERCASE = 3;
const PWD_LENGTH = 12;

export interface Props extends XFormElementProps, /* @vue-ignore */ Omit<PfTextInputProps, 'checked' | 'disabled' | 'type' | 'modelValue'> {
  help?: string;
  nativeErrors?: boolean;
}
</script>

<script lang="ts" setup>
import { useXFormElement, type Props as XFormElementProps, type Emits as XFormElementEmits } from '@common/mixins/XFormElement';
import { pickRandom, shuffle } from '@common/utils';
import type { default as PfTextInput, Props as PfTextInputProps } from '@vue-patternfly/core/components/TextInput.vue';
import { toValue, computed, type Ref } from 'vue';

defineOptions({
  inheritAttrs: false,
});

const props = withDefaults(defineProps<Props>(), {
  type: 'text',
  checked: undefined,
});

const emit = defineEmits<XFormElementEmits>();

const {
  change,
  checkValidity,
  effectiveChecked,
  effectiveDisabled,
  input,
  inputType,
  inputValue,
  isInvalid,
  reportValidity,
  reset,
  update,
  value,
  typeTitle,
  typePattern,
} = useXFormElement(props, emit);

function generatePassword() {
  const pwd = [];
  const allowed_chars = 'abcdefghjkmnpqrstuvwxyz'.split('');
  const allowed_nums = '23456789'.split('');

  const nums = Math.round(Math.random() * PWD_NUMBERS);
  for (let i = 0; i < nums; i++) {
    pwd.push(pickRandom(allowed_nums));
  }

  const upper = Math.round(Math.random() * PWD_UPPERCASE);
  for (let i = 0; i < upper; i++) {
    pwd.push(pickRandom(allowed_chars).toUpperCase());
  }

  for (let i = 0; i < PWD_LENGTH - nums - upper; i++) {
    pwd.push(pickRandom(allowed_chars));
  }

  update(shuffle(pwd).join(''));
}

defineExpose({
  input: computed(() => toValue(input as Ref<InstanceType<typeof PfTextInput> | null>)?.input),
  checkValidity,
  reportValidity,
  reset,
});
</script>
